import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import siteData from "../data/site.json";

const formatTitle = (title) => {
  if (!title) return siteData.defaultTitle || "Manage Your Vastu (MYV)";
  return `${title} | Manage Your Vastu (MYV)`;
};

export default function SEO({
  title,
  description,
  canonical,
  ogType = "website",
  ogImage,
  article,
  faqItems,
}) {
  const location = useLocation();
  const baseUrl = siteData.baseUrl || "";
  const resolvedDescription = description || siteData.defaultDescription || "";
  const resolvedTitle = formatTitle(title);
  const resolvedCanonical =
    canonical || (baseUrl ? `${baseUrl}${location.pathname}` : location.pathname);
  const resolvedOgImage = ogImage || siteData.ogImage || "/og-default.svg";

  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Manage Your Vastu",
    alternateName: "MYV",
    applicationCategory: "BusinessApplication",
    offers: [
      {
        "@type": "Offer",
        price: "1999",
        priceCurrency: "INR",
        name: "Monthly",
      },
      {
        "@type": "Offer",
        price: "15999",
        priceCurrency: "INR",
        name: "Yearly",
      },
    ],
  };

  const articleSchema = article
    ? {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: article.headline,
        description: article.description,
        keywords: article.keywords,
        articleSection: article.articleSection,
        wordCount: article.wordCount,
        author: {
          "@type": "Organization",
          name: "Manage Your Vastu Team",
        },
        publisher: {
          "@type": "Organization",
          name: "MYV",
        },
        mainEntityOfPage: resolvedCanonical,
      }
    : null;

  const faqSchema =
    faqItems && faqItems.length
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqItems.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: item.answer,
            },
          })),
        }
      : null;

  return (
    <Helmet>
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <link rel="canonical" href={resolvedCanonical} />

      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="Manage Your Vastu" />
      <meta property="og:url" content={resolvedCanonical} />
      <meta property="og:image" content={resolvedOgImage} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={resolvedOgImage} />

      <script type="application/ld+json">
        {JSON.stringify(softwareSchema)}
      </script>
      {articleSchema && (
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      )}
      {faqSchema && (
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      )}
      {/* Replace with CMS-driven SEO fields when backend is introduced */}
    </Helmet>
  );
}
